import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/macro';
import { Input2, Label2, Nav2 } from '../Text';

import { Tooltip } from '../Tooltip';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Header = styled.div<{ disabled: boolean }>`
  color: ${({ disabled, theme }) => (disabled ? theme.bg5 : theme.white)};
  margin-top: 0;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
`;

const TooltipWrapper = styled.div`
  margin-left: 0.5rem;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
`;

const Option = styled.button<{ selected: boolean; disabled: boolean }>`
  border: 0.125rem solid rgba(0, 0, 0, 0);
  border-radius: 0.75rem;
  padding: 0.5rem 0.75rem;
  margin-right: 0.25rem;
  transition: all ${({ theme }) => theme.fast} ease-out;
  display: flex;
  flex-direction: row;

  ${({ disabled, theme, selected }) => {
    if (disabled) {
      return `
        background: ${theme.bg3};
        border-color: rgba(0,0,0,0);
        color: ${theme.bg5};
      `;
    }
    if (selected) {
      return `
        background: ${theme.bg4};
        border-color: ${theme.gray2};
        color: ${theme.white};
      `;
    }
    return `
      background: ${theme.bg0};
      border-color: rgba(0,0,0,0);
      color: ${theme.gray3};
    `;
  }}

  &:last-child {
    margin-right: 0;
  }

  ${({ disabled, theme }) => {
    if (!disabled) {
      return `
        &:hover {
          box-shadow: ${theme.hover4};
          cursor: pointer;
        }

        &:enabled:active {
          border-color: rgba(0, 0, 0, 0);
          background: ${theme.bg3};
          box-shadow: none;
        }
      `;
    }
    return '';
  }}

  & > input {
    display: ${({ selected }) => (selected ? 'auto' : 'none')};
  }
`;

const CustomInput = styled(Input2)`
  background: none;
  border: none;
  color: ${({ theme }) => theme.white};
  max-width: 2.5rem;
  margin: 0;
  padding: 0;

  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

interface SlippageInputProps {
  options: number[];
  onChange: (value: number) => void;
  max: number;
  disabled?: boolean;
}

export function SlippageInput({ options, onChange, max, disabled = false }: SlippageInputProps) {
  const { t } = useTranslation();

  const [selected, setSelected] = useState(0);
  const [customInput, setCustomInput] = useState('');
  const [groupFocus, setGroupFocus] = useState(false);
  const inputElem = useRef<HTMLInputElement>(null);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputVal = e.target.value;
    if (Number(inputVal) <= max) {
      setCustomInput(e.target.value);
    }
  };

  const focusGroup = () => {
    setGroupFocus(true);
  };

  const blurGroup = () => {
    setGroupFocus(false);
  };

  useEffect(() => {
    if (selected === options.length && inputElem && groupFocus) {
      inputElem?.current?.focus();
    }
  }, [selected, inputElem, options, groupFocus]);

  useEffect(() => {
    if (selected === options.length) {
      onChange(Number(customInput) / 100);
    } else {
      onChange(options[selected] / 100);
    }
  }, [selected, customInput, onChange, options]);

  useEffect(() => {
    if (disabled) {
      setSelected(-1);
    } else {
      setSelected(0);
    }
  }, [disabled]);

  return (
    <Container onFocus={focusGroup} onBlur={blurGroup}>
      <Header disabled={disabled}>
        <Label2>{t('Slippage tolerance')}</Label2>
        <TooltipWrapper>
          <Tooltip size="m" position="top" offset={0.1} disabled={disabled}>
            {t(
              "Slippage is the difference between the expected price of an order and the price when the order actually executes. Smaller slippage is better, but if it's too small the transaction may fail.",
            )}
          </Tooltip>
        </TooltipWrapper>
      </Header>
      <OptionsContainer>
        {options.map((option, index) => (
          <Option
            key={option}
            selected={selected === index}
            onClick={() => {
              setSelected(index);
            }}
            disabled={disabled}
          >
            <Nav2>{option.toFixed(1)}%</Nav2>
          </Option>
        ))}
        <Option
          selected={selected === options.length}
          onClick={() => {
            setSelected(options.length);
          }}
          disabled={disabled}
        >
          <CustomInput type="number" value={customInput} onChange={handleInput} ref={inputElem} />
          <Nav2>{selected === options.length ? '%' : t('custom')}</Nav2>
        </Option>
      </OptionsContainer>
    </Container>
  );
}

SlippageInput.defaultProps = {
  disabled: false,
};
